import PropTypes from 'prop-types';
import { TableHead, TableRow, Checkbox, TableCell } from '@mui/material';
import { Children, isValidElement, useCallback } from 'react';
import { useListContext, useResourceContext, useTranslate } from 'react-admin';
import clsx from 'clsx';
import CustomDatagridHeaderCell from './CustomDatagridHeaderCell';
import ExpandAllButton from '@/components/global/buttons/ExpandAllButton';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledCheckbox = styled(TableCell)({
    backgroundColor: `${Palette.gray.gray100} !important`,
    padding: '0 0 0 6px !important',
});

const CustomDatagridHeader = props => {
    const { children, className, hasExpand = false, hasBulkActions = false, isRowSelectable } = props;
    const resource = useResourceContext(props);
    const translate = useTranslate();
    const { sort, data, onSelect, selectedIds, setSort } = useListContext(props);

    const updateSortCallback = useCallback(
        event => {
            event.stopPropagation();
            const newField = event.currentTarget.dataset.field;
            const newOrder = sort.field === newField ? (sort.order === 'ASC' ? 'DESC' : 'ASC') : event.currentTarget.dataset.order;

            setSort({ field: newField, order: newOrder });
        },
        [sort.field, sort.order, setSort],
    );

    const updateSort = setSort ? updateSortCallback : null;

    const handleSelectAll = useCallback(
        event =>
            onSelect(
                event.target.checked
                    ? selectedIds.concat(
                          data
                              .filter(record => !selectedIds.includes(record.id))
                              .filter(record => (isRowSelectable ? isRowSelectable(record) : true))
                              .map(record => record.id),
                      )
                    : [],
            ),
        [data, onSelect, isRowSelectable, selectedIds],
    );

    const selectableIds = Array.isArray(data)
        ? isRowSelectable
            ? data.filter(record => isRowSelectable(record)).map(record => record.id)
            : data.map(record => record.id)
        : [];

    return (
        <TableHead className={className}>
            <TableRow>
                {hasExpand && (
                    <CustomDatagridHeaderCell>
                        <ExpandAllButton resource={resource} ids={data.map(record => record.id)} />
                    </CustomDatagridHeaderCell>
                )}
                {hasBulkActions && selectedIds && (
                    <StyledCheckbox>
                        <Checkbox
                            inputProps={{
                                'aria-label': translate('ra.action.select_all', { _: 'Select all' }),
                            }}
                            className='select-all'
                            color='primary'
                            checked={selectedIds.length > 0 && selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id))}
                            onChange={handleSelectAll}
                            onClick={e => e.stopPropagation()}
                        />
                    </StyledCheckbox>
                )}
                {Children.map(children, (field, index) =>
                    isValidElement(field) ? (
                        <CustomDatagridHeaderCell
                            className={clsx(`column-${field.props.source}`)}
                            sort={sort}
                            field={field}
                            isSorting={sort.field === (field.props.sortBy || field.props.source)}
                            key={field.props.source || index}
                            resource={resource}
                            updateSort={updateSort}
                        />
                    ) : null,
                )}
            </TableRow>
        </TableHead>
    );
};

CustomDatagridHeader.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    sort: PropTypes.exact({
        field: PropTypes.string,
        order: PropTypes.oneOf(['ASC', 'DESC']),
    }),
    data: PropTypes.arrayOf(PropTypes.any),
    hasExpand: PropTypes.bool,
    hasBulkActions: PropTypes.bool,
    isRowSelectable: PropTypes.func,
    onSelect: PropTypes.func,
    resource: PropTypes.string,
    selectedIds: PropTypes.arrayOf(PropTypes.any),
    setSort: PropTypes.func,
};

export default CustomDatagridHeader;
